import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowLeftLong, faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Form from '../Form/Form';
import Button from '../Form/Button';
import '../Modal/CreateProductModal.scss';
import './TableFilters.scss';

// Past this many filters the panel switches to 3 per row (and TableSearch widens it) so it
// doesn't turn into a long scroll.
export const MANY_FILTERS_THRESHOLD = 10;

const TableFiltersInner = ({ columns, onReset }) => {
  const { reset } = useFormContext();
  const { t } = useTranslation();
  const isRtl = document.documentElement.dir === 'rtl';
  const filterColumns = columns.filter(col => col.filterable?.component);
  const manyFilters = filterColumns.length > MANY_FILTERS_THRESHOLD;

  // Optional `filterable.group` — a title, or { title, order } — splits the panel into titled
  // blocks, ordered by `order` else by first appearance. Ungrouped filters form an untitled
  // first block, so a listing without groups renders exactly as before.
  const groups = [];
  filterColumns.forEach(col => {
    const def = col.filterable.group;
    const title = (typeof def === 'string' ? def : def?.title) || '';
    let group = groups.find(g => g.title === title);
    if (!group) {
      group = { title, order: title ? (def?.order ?? groups.length) : -1, columns: [] };
      groups.push(group);
    }
    group.columns.push(col);
  });
  groups.sort((a, b) => a.order - b.order);

  const handleResetFilters = () => {
    reset();
    onReset && onReset();
  };

  const renderField = (col) => {
    const { component: FilterComponent, label: filterableLabel, renderInline, filterName, group, ...filterProps } = col.filterable;
    const label = filterableLabel != null ? filterableLabel : col.Header;
    const fieldName = filterName || col.accessor;
    const htmlFor = FilterComponent.isRangeFilter
      ? `${fieldName}.min`
      : fieldName;
    return (
      <div key={col.accessor}>
        <label htmlFor={htmlFor} className="cdm-field-label">{label}</label>
        <FilterComponent
          name={fieldName}
          placeholder={label}
          {...filterProps}
        />
      </div>
    );
  };

  return (
    <>
      {groups.map((group, i) => (
        <div
          key={group.title || '_'}
          className={`cdm-section table-filters${manyFilters ? ' table-filters--3col' : ''}`}
          style={{ marginBottom: i === groups.length - 1 ? 0 : 10 }}
        >
          {group.title && <p className="cdm-section__label">{group.title}</p>}
          <div className="cdm-grid-2">
            {group.columns.map(renderField)}
          </div>
        </div>
      ))}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button className="modal-btn-secondary" type="button" onClick={handleResetFilters}>
          <FontAwesomeIcon icon={isRtl ? faArrowRightLong : faArrowLeftLong} />
          {t('Reset')}
        </Button>
        <Button type="submit" className="modal-btn-primary">
          <FontAwesomeIcon icon={faSearch} />
          {t('Search')}
        </Button>
      </div>
    </>
  );
};

// Reverses the flattening below — rebuilds the raw form shape (nested
// { min, max } for range fields) from the flat filters map so the form
// can be pre-filled when a listing's previous search is restored.
const buildDefaultValues = (columns, filters) => {
  const result = {};
  columns.filter(col => col.filterable?.component).forEach(col => {
    const { component: FilterComponent, filterName } = col.filterable;
    const fieldName = filterName || col.accessor;
    if (FilterComponent.isRangeFilter) {
      const min = filters[`${fieldName}Min`];
      const max = filters[`${fieldName}Max`];
      if (min !== undefined || max !== undefined) {
        result[fieldName] = { min: min ?? '', max: max ?? '' };
      }
    } else if (filters[fieldName] !== undefined) {
      result[fieldName] = filters[fieldName];
    }
  });
  return result;
};

const TableFilters = ({ columns, onFilter, onReset, defaultValues = {} }) => (
  <Form id="filterForm" defaultValues={buildDefaultValues(columns, defaultValues)} onSubmit={(data) => {
    const result = {};
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null && ('min' in value || 'max' in value)) {
        if (value.min !== undefined && value.min !== '') result[`${key}Min`] = value.min;
        if (value.max !== undefined && value.max !== '') result[`${key}Max`] = value.max;
      } else if (value !== undefined && value !== '' && value !== false) {
        result[key] = value;
      }
    });
    onFilter && onFilter(result);
  }}>
    <TableFiltersInner columns={columns} onReset={onReset} />
  </Form>
);

export default TableFilters;
